import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { organizationService } from '../../../services/firebaseService';
import { db } from '../../../firebase';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step1Organization = ({ stepData, onComplete, onValidation, userData, structure, setStructure, onOrganizationAttached }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [formData, setFormData] = useState({
    name: stepData.name || '',
    entityType: stepData.entityType || 'multi-specialty',
    address: stepData.address || '',
    phone: stepData.phone || '',
    email: stepData.email || '',
    totalBeds: stepData.totalBeds || ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [organizationCreated, setOrganizationCreated] = useState(!!stepData.organizationId);

  // Check if organization already exists
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const organizations = await organizationService.getAll();
        if (organizations && organizations.length > 0) {
          const existingOrg = organizations[0];
          setFormData({
            name: existingOrg.name || '',
            entityType: existingOrg.entityType || 'multi-specialty',
            address: existingOrg.address || '',
            phone: existingOrg.phone || '',
            email: existingOrg.email || '',
            totalBeds: existingOrg.totalBeds || ''
          });
          setOrganizationCreated(true);
          onComplete({ ...existingOrg, organizationId: existingOrg.id });
        }
      } catch (error) {
        console.error('Error checking existing organization:', error);
      }
    };
    checkExisting();
  }, []);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('step1.errors.nameRequired');
    }
    
    if (!formData.address.trim()) {
      newErrors.address = t('step1.errors.addressRequired');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('step1.errors.phoneRequired');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('step1.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('step1.errors.emailInvalid');
    }
    
    setErrors(newErrors);
    onValidation(Object.keys(newErrors).length > 0 ? Object.values(newErrors) : null);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSaving(true);
    
    try {
      let organizationId;
      const isNewOrganization = !(organizationCreated && stepData.organizationId);
      const orgPayload = {
        name: formData.name.trim(),
        entityType: formData.entityType,
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        totalBeds: formData.totalBeds,
        updatedAt: new Date().toISOString()
      };

      if (!isNewOrganization) {
        await organizationService.update(stepData.organizationId, orgPayload);
        organizationId = stepData.organizationId;
      } else {
        const createdOrg = await organizationService.create({
          ...orgPayload,
          createdAt: new Date().toISOString()
        });
        organizationId = createdOrg.id;

        // Silently attach this new organizationId to the current user's own profile
        // (relevant for superadmin accounts created by bootstrapPlatform, which start
        // with no organizationId). No-op for normal hospital admins whose account
        // already has an organizationId — onOrganizationAttached is only passed down
        // when the App-level renderDashboard() computed forceSetupWizard as true.
        if (userData?.uid) {
          try {
            await updateDoc(doc(db, 'users', userData.uid), {
              organizationId,
              updated_at: serverTimestamp()
            });
            console.log('Organization attached to user profile:', organizationId);
          } catch (attachError) {
            console.error('Failed to attach organizationId to user profile:', attachError);
            // Non-fatal — organization itself was created successfully.
            // OrgContext will not re-resolve immediately, but the organization exists
            // and can be attached manually if needed.
          }
        }

        if (typeof onOrganizationAttached === 'function') {
          onOrganizationAttached(organizationId);
        }
      }
      
      // Update structure
      if (setStructure) {
        setStructure(prev => ({
          ...prev,
          organizations: [{
            id: organizationId,
            ...orgPayload
          }]
        }));
      }
      
      setOrganizationCreated(true);
      onComplete({ 
        organizationId,
        name: orgPayload.name,
        entityType: orgPayload.entityType,
        address: orgPayload.address,
        phone: orgPayload.phone,
        email: orgPayload.email,
        totalBeds: orgPayload.totalBeds
      });
      
      notify.success(t('step1.notifications.createdSuccess'), t('step1.notifications.createdSuccessTitle'));
    } catch (error) {
      console.error('Error creating organization:', error);
      notify.error(t('step1.notifications.createFailed'), t('step1.notifications.createFailedTitle'));
      setErrors({ general: error.message || 'Failed to create organization' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step1.title')}</h3>
        <p>{t('step1.description')}</p>
      </div>

      {errors.general && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '6px' }}>
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="name">
              {t('step1.organizationName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={errors.name ? 'error' : ''}
              placeholder={t('step1.namePlaceholder')}
              required
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="entityType">
              {t('step1.entityType')} <span className="required">*</span>
            </label>
            <select
              id="entityType"
              value={formData.entityType}
              onChange={(e) => handleChange('entityType', e.target.value)}
              required
            >
              <option value="multi-specialty">{t('step1.entityTypes.multiSpecialty')}</option>
              <option value="specialty">{t('step1.entityTypes.specialty')}</option>
              <option value="community">{t('step1.entityTypes.community')}</option>
              <option value="teaching">{t('step1.entityTypes.teaching')}</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label htmlFor="address">
              {t('step1.address')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className={errors.address ? 'error' : ''}
              placeholder={t('step1.addressPlaceholder')}
              required
            />
            {errors.address && <span className="error-text">{errors.address}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              {t('step1.phone')} <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className={errors.phone ? 'error' : ''}
              placeholder={t('step1.phonePlaceholder')}
              required
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              {t('step1.email')} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              placeholder={t('step1.emailPlaceholder')}
              required
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="totalBeds">{t('step1.totalBeds')}</label>
            <input
              type="number"
              id="totalBeds"
              value={formData.totalBeds}
              onChange={(e) => handleChange('totalBeds', e.target.value)}
              placeholder={t('step1.totalBedsPlaceholder')}
              min="0"
            />
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? t('step1.creating') : organizationCreated ? t('step1.updateOrganization') : t('step1.createOrganization')}
          </button>
        </div>
      </form>

      {organizationCreated && (
        <div className="success-message" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '6px' }}>
          {t('step1.successBanner')}
        </div>
      )}
    </div>
  );
};

export default Step1Organization;
